import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { getAuthToken } from '@/api';
import {
  type NotificationStreamEvent,
  NotificationStreamHTTPError,
  openNotificationStream,
} from '@/api/notificationStream';

export type NotificationStreamState = 'idle' | 'connecting' | 'open' | 'down';
export type NotificationStreamListener = (event: NotificationStreamEvent) => void;

/** Waits in seconds between reconnect attempts; the last value repeats. */
export const RECONNECT_DELAYS_SECONDS = [1, 2, 4, 8, 16, 30];

export interface NotificationStreamValue {
  state: NotificationStreamState;
  subscribe: (listener: NotificationStreamListener) => () => void;
}

interface NotificationStreamConnection {
  subscribe: (listener: NotificationStreamListener) => () => void;
  stop: () => void;
}

/**
 * Opens one stream and keeps it alive with exponential backoff. The backoff
 * resets after a successful open. A 401 stops reconnecting and reports `down`.
 */
export function startNotificationStream(
  onState: (state: NotificationStreamState) => void,
): NotificationStreamConnection {
  const listeners = new Set<NotificationStreamListener>();
  let active = true;
  let attempt = 0;
  let abortController: AbortController | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const setState = (state: NotificationStreamState) => {
    if (active) {
      onState(state);
    }
  };

  const scheduleReconnect = () => {
    const index = Math.min(attempt, RECONNECT_DELAYS_SECONDS.length - 1);
    attempt += 1;
    timer = setTimeout(() => {
      timer = null;
      void connect();
    }, RECONNECT_DELAYS_SECONDS[index] * 1000);
  };

  const connect = async () => {
    if (!active) {
      return;
    }

    const token = getAuthToken();
    if (!token) {
      setState('down');

      return;
    }

    abortController = new AbortController();
    setState('connecting');

    try {
      await openNotificationStream({
        token,
        signal: abortController.signal,
        onOpen: () => {
          attempt = 0;
          setState('open');
        },
        onNotification: (event) => {
          if (!active) {
            return;
          }
          for (const listener of [...listeners]) {
            listener(event);
          }
        },
      });
    } catch (err: unknown) {
      if (!active || isAbortError(err)) {
        return;
      }

      setState('down');
      if (err instanceof NotificationStreamHTTPError && err.status === 401) {
        return;
      }
      scheduleReconnect();

      return;
    }

    if (active) {
      setState('down');
      scheduleReconnect();
    }
  };

  void connect();

  return {
    subscribe: (listener) => {
      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    },
    stop: () => {
      active = false;
      listeners.clear();
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      abortController?.abort();
    },
  };
}

function isAbortError(err: unknown): boolean {
  return !!err && typeof err === 'object' && 'name' in err && err.name === 'AbortError';
}

export const NotificationStreamContext = createContext<NotificationStreamValue | null>(null);

export function NotificationStreamProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<NotificationStreamState>('idle');
  const [subscribe, setSubscribe] = useState<NotificationStreamValue['subscribe']>(
    () => () => () => {},
  );

  useEffect(() => {
    const connection = startNotificationStream(setState);
    setSubscribe(() => connection.subscribe);

    return () => {
      connection.stop();
    };
  }, []);

  const value = useMemo(() => ({ state, subscribe }), [state, subscribe]);

  return (
    <NotificationStreamContext.Provider value={value}>
      {children}
    </NotificationStreamContext.Provider>
  );
}

/** Returns the shared stream, or null outside a provider. */
export function useOptionalNotificationStream(): NotificationStreamValue | null {
  return useContext(NotificationStreamContext);
}

export function useNotificationStreamContext(): NotificationStreamValue {
  const value = useContext(NotificationStreamContext);
  if (!value) {
    throw new Error('useNotificationStreamContext must be used within NotificationStreamProvider');
  }

  return value;
}
